import React, { useState, useEffect } from "react";
import {
  Checkbox,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Typography,
  Select,
  Option
} from "@material-tailwind/react";
import moment from "moment";
import Calendar from "./Calendar";
import { DateRange } from "./DatePicker/DateTimePicker";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { updateMaintenance } from "../../store/slices/maintenanceSlice";

interface MaintenanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    startTime: string;
    endTime: string;
    note: string;
    selectedServices: number[];
  }) => void;
  services: Array<{
    id: number;
    name: string;
    containerName?: string; // Added containerName for API interaction
  }>;
  selectedServiceId?: number | null;
}

const MaintenanceModal: React.FC<MaintenanceModalProps> = ({
  isOpen,
  onClose,
  onSave,
  services,
  selectedServiceId,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { cluster } = useSelector((state: RootState) => state.maintenance);
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: new Date(),
    endDate: moment().add(1, "day").toDate(),
  });
  const [note, setNote] = useState("");
  const [selectedServices, setSelectedServices] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [showServices, setShowServices] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize with selected service when modal opens
  useEffect(() => {
    if (isOpen) {
      if (selectedServiceId) {
        setSelectedServices([selectedServiceId]);
        setSelectAll(false);
      } else {
        setSelectedServices([]);
        setSelectAll(false);
      }
    }
  }, [isOpen, selectedServiceId]);

  const handleDateRangeChange = (newDateRange: DateRange) => {
    setDateRange(newDateRange);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedServices([]);
    } else {
      setSelectedServices(services.map((service) => service.id));
    }
    setSelectAll(!selectAll);
  };

  const handleServiceToggle = (serviceId: number) => {
    setSelectedServices((prev) =>
      prev.includes(serviceId)
        ? prev.filter((id) => id !== serviceId)
        : [...prev, serviceId]
    );

    // Update selectAll state based on whether all services are selected
    const updatedServices = selectedServices.includes(serviceId)
      ? selectedServices.filter((id) => id !== serviceId)
      : [...selectedServices, serviceId];

    setSelectAll(updatedServices.length === services.length);
  };

  const handleSave = async () => {
    setIsSubmitting(true);
    
    try {
      // Get container names for selected services
      const serviceContainerNames = selectedServices
        .map(id => services.find(s => s.id === id)?.containerName)
        .filter(name => name) as string[];
      
      // Determine if this is a global maintenance or service-specific
      // If modal is opened from Global Maintenance button (selectedServiceId is null)
      // and all services are selected OR no services are selected, it's global
      const isGlobalButton = selectedServiceId === null;
      const isAllServicesSelected = selectedServices.length === services.length;
      const isNoServicesSelected = selectedServices.length === 0;
      
      // Type is 'global' if:
      // 1. Modal was opened by clicking "Global Maintenance" button
      // 2. AND either all services are selected OR no services are selected
      const maintenanceType = 
        isGlobalButton && (isAllServicesSelected || isNoServicesSelected) 
          ? 'global' 
          : 'service';
      
      
      // Format start and end times for API - properly convert local time to UTC
      const startTime = dateRange.startDate
        ? moment(dateRange.startDate).utc().format("YYYY-MM-DDTHH:mm:ss") + "Z"
        : null;
        
      const endTime = dateRange.endDate
        ? moment(dateRange.endDate).utc().format("YYYY-MM-DDTHH:mm:ss") + "Z"
        : null;
      
      // Create maintenance request
      await dispatch(updateMaintenance({
        enabled: true, // Always true when saving from modal (since we're enabling maintenance)
        type: maintenanceType,
        cluster: cluster || 'dev', // Use current cluster or default to 'dev'
        // Only include services array for 'service' type maintenance
        services: maintenanceType === 'service' ? serviceContainerNames : [],
        start_time: startTime,
        end_time: endTime,
        reason: note
      })).unwrap();
      // Also call the original onSave for backward compatibility
      onSave({
        startTime: dateRange.startDate
          ? moment(dateRange.startDate).format("YYYY-MM-DD HH:mm")
          : "",
        endTime: dateRange.endDate
          ? moment(dateRange.endDate).format("YYYY-MM-DD HH:mm")
          : "",
        note,
        selectedServices,
      });
    } catch (error: any) {
      console.error('Failed to save maintenance mode:', error);
    } finally {
      setIsSubmitting(false);
      onClose();
    }
  };

  const handleUseTemplate = (hours: number) => {
    const now = new Date();
    const end = moment().add(hours, "hours").toDate();

    setDateRange({
      startDate: now,
      endDate: end,
    });
  };

  // Update the handleTemplateChange function to accept a string value
  const handleTemplateChange = (value: string | undefined) => {
    if (value) {
      const hours = parseInt(value);
      if (!isNaN(hours)) {
        handleUseTemplate(hours);
        setSelectedTemplate(value);
      }
    }
  };

  return (
    <Dialog
      open={isOpen}
      handler={onClose}
      size="md"
      className="bg-white rounded-lg shadow-xl max-h-[90vh] overflow-y-auto"
      placeholder=""
      onResize={() => {}}
      onResizeCapture={() => {}}
      onPointerEnterCapture={() => {}}
      onPointerLeaveCapture={() => {}}
    >
      <DialogHeader
        className="flex justify-between items-center border-b border-gray-200"
        placeholder=""
        onResize={() => {}}
        onResizeCapture={() => {}}
        onPointerEnterCapture={() => {}}
        onPointerLeaveCapture={() => {}}
      >
        <div>
          {selectedServiceId ? (
            <>
              <Typography
                variant="h3"
                className="font-bold text-2xl text-gray-900 font-inter"
                placeholder={undefined}
                onResize={undefined}
                onResizeCapture={undefined}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              >
                {services.find((s) => s.id === selectedServiceId)?.name ||
                  "Service"}
              </Typography>
              <Typography
                className="font-semibold text-sm text-gray-500 font-inter"
                placeholder={undefined}
                onResize={undefined}
                onResizeCapture={undefined}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              >
                Schedule maintenance for this service
              </Typography>
            </>
          ) : (
            <>
              <Typography
                variant="h3"
                className="font-bold text-2xl text-gray-900 font-inter"
                placeholder={undefined}
                onResize={undefined}
                onResizeCapture={undefined}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              >
                Global Maintenance Mode
              </Typography>
              <Typography
                className="font-semibold text-sm text-gray-500 font-inter"
                placeholder={undefined}
                onResize={undefined}
                onResizeCapture={undefined}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              >
                Schedule maintenance for multiple services
              </Typography>
            </>
          )}
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          onClick={onClose}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </DialogHeader>

      <DialogBody
        className="p-4 overflow-y-visible"
        placeholder=""
        onResize={() => {}}
        onResizeCapture={() => {}}
        onPointerEnterCapture={() => {}}
        onPointerLeaveCapture={() => {}}
      >
        {/* Date Range Picker */}
        <div className="mb-6">
          <label className="block font-normal text-sm text-gray-500 font-roboto mb-2">
            Date Range
          </label>
          <Calendar
            onDateRangeChange={handleDateRangeChange}
            dateRange={dateRange}
          />
        </div>

        <div className="mb-6">
          <label className="block font-normal text-sm text-gray-500 font-roboto mb-2">
            Template
          </label>
          <Select
            value={selectedTemplate}
            onChange={handleTemplateChange}
            placeholder="Select a template"
            className="w-full box-border flex flex-row justify-between items-center p-[10px_12px] gap-2 border border-gray-300 rounded-lg bg-white focus:border-[#6172F3] focus:ring-1 focus:ring-[#6172F3] focus:outline-none"
            onResize={() => {}}
            onResizeCapture={() => {}}
            onPointerEnterCapture={() => {}}
            onPointerLeaveCapture={() => {}}
          >
            <Option value="">Select a template</Option>
            <Option value="24">24 hours</Option>
            <Option value="48">48 hours</Option>
            <Option value="72">72 hours</Option>
          </Select>
        </div>

        <div className="mb-6">
          <label className="block font-normal text-sm text-gray-500 font-roboto mb-1">
            Note
          </label>
          <textarea
            className="w-full box-border flex flex-row items-start p-[15px_16px] gap-2 border border-gray-300 rounded-lg focus:border-[#6172F3] focus:ring-1 focus:ring-[#6172F3] focus:outline-none"
            rows={3}
            placeholder="Example message"
            value={note}
            onChange={(e) => setNote(e.target.value)}
        
          ></textarea>
        </div>

        {!selectedServiceId && (
          <div className="mb-2">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <Checkbox
                  id="select-all"
                  color="blue"
                  checked={selectAll}
                  onChange={handleSelectAll}
                  label={
                    <span className="font-bold text-sm text-gray-500 font-roboto">
                      Select all services
                    </span>
                  }
                  crossOrigin="anonymous"
                  onResize={() => {}}
                  onResizeCapture={() => {}}
                  onPointerEnterCapture={() => {}}
                  onPointerLeaveCapture={() => {}}
                  className="checked:bg-[#6172F3] w-[18px] h-[18px]"
                />
              </div>
              <a
                onClick={() => setShowServices(!showServices)}
                className="font-bold text-sm underline text-[#6172F3] font-roboto cursor-pointer"
              >
                {showServices ? "Close Options" : "Open Options"}
              </a>
            </div>

            {showServices && (
              <div className="overflow-y-auto max-h-60 border border-gray-200 rounded-md p-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {services.map((service) => (
                    <div key={service.id} className="flex items-center p-1 hover:bg-gray-50 rounded">
                      <Checkbox
                        id={`service-${service.id}`}
                        color="blue"
                        checked={selectedServices.includes(service.id)}
                        onChange={() => handleServiceToggle(service.id)}
                        crossOrigin="anonymous"
                        onResize={() => {}}
                        onResizeCapture={() => {}}
                        onPointerEnterCapture={() => {}}
                        onPointerLeaveCapture={() => {}}
                        className="checked:bg-[#6172F3] w-[18px] h-[18px] mr-2"
                      />
                      <label
                        htmlFor={`service-${service.id}`}
                        className="font-semibold text-xs flex items-center text-gray-500 font-roboto truncate"
                      >
                        {service.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogBody>

      <DialogFooter
        className="flex justify-end p-4 border-t border-gray-200 gap-2"
        placeholder=""
        onResize={() => {}}
        onResizeCapture={() => {}}
        onPointerEnterCapture={() => {}}
        onPointerLeaveCapture={() => {}}
      >
        <button
          onClick={onClose}
          className="px-4 py-2 bg-white border border-[#6172F3] rounded-md shadow-sm text-sm font-medium text-[#6172F3] hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={isSubmitting || !dateRange.startDate || !dateRange.endDate}
          className={`px-4 py-2 bg-[#6172F3] border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${(isSubmitting || !dateRange.startDate || !dateRange.endDate) ? 'opacity-70 cursor-not-allowed' : ''}`}
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </button>
      </DialogFooter>
    </Dialog>
  );
};

export default MaintenanceModal;
